import React, { useEffect, useRef, useState } from 'react';
import GreyWrapper from '../../../common/GreyWrapper';
import {
  newEmailHeader,
  newEmailFooter,
  basic_body
} from '../../../../../../../constants/email_template.js';
import { post_api } from '../../../../../../../redux/api_funcs';
import { get_api_url } from '../../../../../../../utils/urls.js';

interface EmailShareProps {
  emailSubject: string;
  emailContent: string;
  email: string;
  onSend: (p: string) => void;
  isEmailSent: boolean;
}

const EmailShare: React.FC<EmailShareProps> = ({
  emailSubject,
  emailContent,
  email,
  onSend,
  isEmailSent
}) => {
  const originalHtmlRef = useRef<string>(emailContent || basic_body);
  const [displayText, setDisplayText] = useState<string>('');
  const [emailSent, setEmailSent] = useState<boolean>(!!isEmailSent);

  const SPLIT_NEWLINE = '\n\n';

  useEffect(() => {
    const html = emailContent || basic_body;
    originalHtmlRef.current = html;

    const temp = document.createElement('div');
    temp.innerHTML = html;
    const walker = document.createTreeWalker(temp, NodeFilter.SHOW_TEXT, null);
    const parts: string[] = [];
    let node: Node | null = walker.nextNode();
    while (node) {
      const raw = node.textContent || '';
      const text = raw.replace(/\s+/g, ' ').trim();
      if (text.length > 0) {
        parts.push(text);
      }
      node = walker.nextNode();
    }

    setDisplayText(parts.join(SPLIT_NEWLINE));
  }, [emailContent]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDisplayText(e.target.value);
  };

  const onSendEmail = async () => {
    try {
      const temp = document.createElement('div');
      temp.innerHTML = originalHtmlRef.current;

      const walker = document.createTreeWalker(temp, NodeFilter.SHOW_TEXT, null);
      const nodes: Node[] = [];
      let n: Node | null = walker.nextNode();
      while (n) {
        const raw = n.textContent || '';
        const text = raw.replace(/\s+/g, ' ').trim();
        if (text.length > 0) {
          nodes.push(n);
        }
        n = walker.nextNode();
      }

      const parts = displayText
        ? displayText.split(SPLIT_NEWLINE).map((p) => p.replace(/\s+/g, ' ').trim())
        : [];

      const min = Math.min(parts.length, nodes.length);
      for (let i = 0; i < min; i++) {
        nodes[i].textContent = parts[i];
      }
      if (parts.length > nodes.length) {
        const extra = parts.slice(nodes.length).join(SPLIT_NEWLINE);
        temp.appendChild(document.createTextNode(extra));
      }

      // Replace any placeholder 'email-address' in the footer with the recipient email
      const footerWithEmail = (newEmailFooter || '').replace(/email-address/g, email);
      const finalTemplate = `${newEmailHeader}${temp.innerHTML}${footerWithEmail}`;

      const url = get_api_url('send_email_cms');
      const payload = {
        to_address: [email],
        subject: emailSubject,
        email_body: `${finalTemplate}`,
        email_type: 'other',
        email_data: [] as any[]
      };

      const response = await post_api(url, payload, true);
      if (response.data.status === 'success') {
        setEmailSent(true);
        originalHtmlRef.current = finalTemplate;
        onSend('email');
      }
    } catch (e: any) {
      console.log(e, 'error sending mail');
    }
  };

  return (
    <>
      <div className="flex flex-1">
        <GreyWrapper customStyle="flex flex-col justify-between flex-1">
          <div>
            <h3 className="text-[15px] font-[550] leading-[18px] text-[#333] mb-2">
              Share via Email
            </h3>

            <div className="bg-white border border-gray-200 rounded-md w-full px-3 py-2 text-sm mb-3">
              {emailSubject}
            </div>

            {/* Editable textarea for email content */}
            <textarea
              value={displayText}
              onChange={handleChange}
              disabled={emailSent}
              className={`w-full min-h-[180px] border border-gray-200 rounded-md p-3 text-[15px] font-medium leading-[20px] text-[#321A50] resize-y focus:outline-none focus:ring-2 focus:ring-orange-400 ${emailSent ? 'opacity-60 cursor-not-allowed bg-white' : ''}`}
            />
          </div>

          <div className="flex">
            <button
              className={`flex items-center justify-center m-0 mt-4 w-full text-[16px] py-2 rounded-[14px] bg-[#EE731B] outline-none border-0 font-[550] ${!emailSent ? 'disabled:opacity-60 disabled:cursor-not-allowed' : ''} ${emailSent ? 'cursor-not-allowed hover:text-[#EE731B] bg-white text-[#EE731B] border border-[#E7E7E7]' : 'mt-4 w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 rounded-[14px] bg-[#EE731B] outline-none border-0 hover:bg-[#CC4B11] hover:text-white cursor-pointer'}`}
              onClick={onSendEmail}
              disabled={emailSent}
            >
              {emailSent ? (
                <span className="inline-flex items-center justify-center gap-[8px]">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <g clipPath="url(#clip0_712_1767)">
                      <rect width="18" height="18" rx="9" fill="white" />
                      <path
                        d="M9 0C4.02891 0 0 4.02891 0 9C0 13.9711 4.02891 18 9 18C13.9711 18 18 13.9711 18 9C18 4.02891 13.9711 0 9 0ZM13.0711 7.44609L8.57109 11.9461C8.37773 12.1395 8.12813 12.2344 7.875 12.2344C7.62187 12.2344 7.37086 12.1388 7.17855 11.9465L4.92855 9.69652C4.545 9.31191 4.545 8.68788 4.92855 8.30327C5.31316 7.91866 5.93719 7.91866 6.3218 8.30327L7.875 9.85781L11.6789 6.05391C12.0635 5.6693 12.6875 5.6693 13.0721 6.05391C13.4543 6.43711 13.4543 7.06289 13.0711 7.44609Z"
                        fill="#EE731B"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_712_1767">
                        <rect width="18" height="18" rx="9" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span>Email Sent</span>
                </span>
              ) : (
                'Send Email'
              )}
            </button>
          </div>
        </GreyWrapper>
      </div>
    </>
  );
};

export default EmailShare;
